import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogIn, LogOut, User, PackageSearch, FileText, FileSignature, FolderKanban, Gift } from "lucide-react";
import { toast } from "sonner";
import { navigation } from "../../data/mock";
import { useAuth } from "../../context/AuthContext";
import ThemeToggle from "./ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, logout } = useAuth();

  const handleSignIn = async () => {
    try {
      const signedInUser = await login();
      if (signedInUser) {
        navigate(signedInUser.is_employee ? "/employees" : "/portal");
      }
    } catch (err) {
      toast.error("Sign-in failed. Please try again.");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-border/5 shadow-[0_2px_30px_rgba(255, 0, 15,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <img
            src="/assets/qp-logo.png"
            alt="Quality Power logo"
            className="w-9 h-9 object-contain shrink-0 group-hover:drop-shadow-[0_0_12px_rgba(255, 0, 15,0.5)] transition-all"
          />
          <span className="font-display font-semibold tracking-wide text-foreground text-lg hidden sm:inline whitespace-nowrap">
            QUALITY POWER
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {navigation.map((item) => (
            <div
              key={item.label}
              className="relative group"
              onMouseEnter={() => setOpenDropdown(item.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <NavLink
                to={item.to || item.items[0].to}
                className={({ isActive }) =>
                  `px-2.5 py-2 text-sm font-medium tracking-wide flex items-center gap-1 rounded-md transition-colors whitespace-nowrap ${
                    isActive ? "text-qcyan" : "text-muted-foreground hover:text-qcyan"
                  }`
                }
              >
                {item.label}
                {item.items && <ChevronDown size={14} className="opacity-60" />}
              </NavLink>
              {item.items && openDropdown === item.label && (
                <div className="absolute top-full left-0 pt-2 min-w-[240px] animate-in fade-in">
                  <div className="glass-panel rounded-xl p-2 border-border/10">
                    {item.items.map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.to}
                        className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-qcyan hover:bg-white/5 rounded-lg transition-colors"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <ThemeToggle />
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-border/10 hover:border-qcyan/40 transition-colors outline-none">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-qcyan/10 border border-qcyan/30 text-qcyan flex items-center justify-center text-xs font-semibold">
                    {user?.name?.[0] || "U"}
                  </span>
                )}
                <span className="text-sm text-foreground max-w-[120px] truncate">{user?.name?.split(" ")[0]}</span>
                <ChevronDown size={14} className="text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-navy-panel border-border/10">
                <DropdownMenuLabel className="text-muted-foreground text-xs">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/portal/account" className="cursor-pointer"><User size={15} /> My Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/orders" className="cursor-pointer"><PackageSearch size={15} /> PO / Orders</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/invoices" className="cursor-pointer"><FileText size={15} /> Invoices</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/quotations" className="cursor-pointer"><FileSignature size={15} /> Quotations</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/projects" className="cursor-pointer"><FolderKanban size={15} /> Projects</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/rewards" className="cursor-pointer"><Gift size={15} /> Rewards</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 focus:text-red-300">
                  <LogOut size={15} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={handleSignIn}
              className="btn-outline-glow px-3.5 py-2.5 rounded-md text-sm font-semibold tracking-wide inline-flex items-center gap-2 whitespace-nowrap"
            >
              <LogIn size={15} /> Sign In
            </button>
          )}
          <Link to="/contact" className="btn-glow-primary px-4 py-2.5 rounded-md text-sm font-semibold tracking-wide whitespace-nowrap">
            Contact Us
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            className="text-foreground"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background border-t border-border/5 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navigation.map((item) => (
              <div key={item.label} className="border-b border-border/5 py-2">
                <button
                  className="w-full flex items-center justify-between text-foreground font-medium py-2"
                  onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                >
                  <Link to={item.to || (item.items && item.items[0].to)}>{item.label}</Link>
                  {item.items && (
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenDropdown(openDropdown === item.label ? null : item.label);
                      }}
                    />
                  )}
                </button>
                {item.items && openDropdown === item.label && (
                  <div className="pl-4 flex flex-col gap-1 pb-2">
                    {item.items.map((sub) => (
                      <Link key={sub.label} to={sub.to} className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" className="btn-glow-primary text-center mt-4 px-5 py-3 rounded-md text-sm font-semibold">
              Contact Us
            </Link>

            {isAuthenticated ? (
              <div className="mt-4 border-t border-border/10 pt-4">
                <div className="flex items-center gap-3 mb-3">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <span className="w-9 h-9 rounded-full bg-qcyan/10 border border-qcyan/30 text-qcyan flex items-center justify-center text-sm font-semibold">
                      {user?.name?.[0] || "U"}
                    </span>
                  )}
                  <div>
                    <p className="text-sm text-foreground font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Link to="/portal" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">Portal Overview</Link>
                  <Link to="/portal/account" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">My Account</Link>
                  <Link to="/portal/orders" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">PO / Orders</Link>
                  <Link to="/portal/invoices" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">Invoices</Link>
                  <Link to="/portal/projects" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">Projects</Link>
                  <Link to="/portal/rewards" className="text-muted-foreground py-1.5 text-sm hover:text-qcyan">Rewards</Link>
                  <button onClick={logout} className="text-left text-red-400 py-1.5 text-sm hover:text-red-300">Sign Out</button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleSignIn}
                className="btn-outline-glow text-center mt-3 px-5 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <LogIn size={15} /> Sign In
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
